import { useEffect } from 'react';
import type { MutableRefObject } from 'react';

// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../../bridge/utils/tableEditorSessionUtils';
import { MNHotInstance } from '../../bridge/tableHotInstanceTypes';

/**
 * Custom hook for managing table selection side effects.
 */
export const useTableSelectionEffects = ({
    elementId,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    isSingleSelected,
    isResizing,
    isReadOnly,
}: {
    elementId: string;
    currentlyEditingEditorId?: string | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    isSingleSelected: boolean;
    isResizing: boolean;
    isReadOnly: boolean;
}): void => {
    /**
     * Handsontable should only listen to keyboard shortcuts when there is one or more cell selections in the table
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance || isReadOnly) return;

        const handleDeselectUnlisten = () => {
            // HOT can fire `afterDeselect` before its internal selection state and focus bookkeeping have fully
            // settled, so defer the unlisten until the current callback stack has completed.
            setTimeout(() => {
                const hasSelections = !!hotInstance.getSelected()?.length;
                if (hasSelections) return;

                hotInstance.unlisten();
            });
        };

        hotInstance.addHook('afterDeselect', handleDeselectUnlisten);

        return () => {
            hotInstance.removeHook('afterDeselect', handleDeselectUnlisten);
        };
    }, [hotTableInstanceRef, isReadOnly, isSingleSelected]);

    /**
     * Deselect cells in table when the table is no longer in a state where the grid should stay active, such as:
     * - When selection has moved to table element title/caption
     * - When table element is no longer selected
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance || isResizing) return;

        const isEditingCellOrGrid =
            !currentlyEditingEditorId ||
            currentlyEditingEditorId === getTableGridEditorId(elementId) ||
            currentlyEditingEditorId === getTableCellEditorId(elementId);

        if (!isReadOnly && isSingleSelected && isEditingCellOrGrid) return;

        hotInstance.deselectCell();
    }, [elementId, currentlyEditingEditorId, hotTableInstanceRef, isReadOnly, isResizing, isSingleSelected]);
};

export default useTableSelectionEffects;
